import { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";

import MessageModal from "../messageModal";
import ErrorModal from "../errorModal";
import Modal from "../modal";

import { W3Context } from "../../services/context";
import getContracts from "../../services/contract/getContracts";
import { sendBidAuctionEvent } from "../../services/gtm";

import { DEFAULT_ERROR_MESSAGE } from "../../utils/util";

import "./placeBidModalStyles.css";

const { geosContract, auctionsContract } = getContracts();

const QUICKSWAP_URL = `https://quickswap.exchange/#/swap?outputCurrency=${geosContract.address}`;

const PlaceBidModal = ({
  displayModal,
  setDisplayModal,
  setUpdateData,
  currentValue,
  tokenId,
  GAS_FEE,
}) => {
  const MIN_BID_VALUE = Math.ceil(currentValue * 1.05);

  const { account, library } = useContext(W3Context);
  const [displayMessageModal, setDisplayMessageModal] = useState(false);
  const [displayErrorModal, setDisplayErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);

  const [messageModalBody, setMessageModalBody] = useState("");
  const [messageModalTitle, setMessageModalTitle] = useState("");
  const [messageModalButtonText, setMessageModalButtonText] = useState("");

  const [bid, setBid] = useState(0);
  const [hasBidError, setHasBidError] = useState(true);
  const [handlerQuickswapFunction, setHandlerQuickswapFunction] =
    useState(undefined);
  const [handlerBidSuccessFunction, setHandlerBidSuccessFunction] =
    useState(undefined);

  const history = useHistory();

  // target="_blank" vulnerability
  // https://medium.com/@jitbit/target-blank-the-most-underestimated-vulnerability-ever-96e328301f4c
  const redirectToQuickSwap = () => {
    const newWindow = window.open(
      QUICKSWAP_URL,
      "_blank",
      "noopener,noreferrer"
    );
    if (newWindow) newWindow.opener = null;
  };

  const redirectToHome = () => {
    history.push("/");
  };

  const onChangeBid = (event) => {
    const value = event?.target?.value;
    if (
      !value ||
      isNaN(value) ||
      parseInt(value) <= 0 ||
      parseInt(value) < MIN_BID_VALUE
    ) {
      setHasBidError(true);
      return;
    }
    setHasBidError(false);
    setBid(parseInt(value));
  };

  const onKeyPressBid = (e) => !/[0-9]/.test(e.key) && e.preventDefault();

  const setMessageModalAttributes = (
    title,
    body,
    buttonText,
    handlerQuickswapFunction = false,
    handlerBidSuccessFunction = false
  ) => {
    setMessageModalTitle(title);
    setMessageModalBody(body);
    setMessageModalButtonText(buttonText);

    setHandlerQuickswapFunction(handlerQuickswapFunction);
    setHandlerBidSuccessFunction(handlerBidSuccessFunction);

    setDisplayMessageModal(true);
  };

  const placeBid = async ({ tokenId, amount }) => {
    const auctionsContractInstance = new library.eth.Contract(
      auctionsContract.abi,
      auctionsContract.address
    );

    const myAddress = library.utils.toChecksumAddress(account);

    await auctionsContractInstance.methods
      .bid(tokenId, amount)
      .send({
        from: myAddress,
        gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
      })
      .then((res) => {
        setUpdateData(res);

        setMessageModalAttributes(
          "Your bid was successfully placed",
          "You'll be notified if your bid is exceeded or if you win the auction",
          "Awesome!",
          false,
          true
        );
        setBid(0);
        setHasBidError(false);
        setDisplayModal(false);

        sendBidAuctionEvent();
        return;
      })
      .catch((err) => {
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        setDisplayErrorModal(true);
        console.log(err);
      });
  };

  const approveContractForGeos = async ({ tokenId, bid }) => {
    const geosContractInstance = new library.eth.Contract(
      geosContract.abi,
      geosContract.address
    );

    const myAddress = library.utils.toChecksumAddress(account);
    const auctionsContractAddress = library.utils.toChecksumAddress(
      auctionsContract.address
    );

    try {
      const balance = await geosContractInstance.methods
        .balanceOf(myAddress)
        .call();

      const availableGeos = parseFloat(library.utils.fromWei(balance, "ether"));

      if (bid > availableGeos) {
        setMessageModalAttributes(
          "You don't have enough GEO$",
          "Buy more and come back to place your bid",
          "Go to QuickSwap!",
          true,
          false
        );

        return;
      }

      const approved = await geosContractInstance.methods
        .allowance(myAddress, auctionsContractAddress)
        .call();

      const approvedGeos = parseFloat(library.utils.fromWei(approved, "ether"));
      const amount = library.utils.toWei(String(bid), "ether");

      if (bid > approvedGeos) {
        await geosContractInstance.methods
          .approve(auctionsContractAddress, amount)
          .send({
            from: myAddress,
            gasPrice: library.utils.toWei(GAS_FEE, "gwei"),
          })
          .then((res) => {
            placeBid({ tokenId, amount });
          })
          .catch((err) => {
            setErrorMessage(DEFAULT_ERROR_MESSAGE);
            setDisplayErrorModal(true);
            console.log(err);
          });
        return;
      }

      placeBid({ tokenId, amount });
    } catch (err) {
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setDisplayErrorModal(true);
      console.log(err);
    }
  };

  const onClickConfirm = async () => {
    if (bid <= 0) {
      setHasBidError(true);
      return;
    }

    setHasBidError(false);

    if (bid < MIN_BID_VALUE) {
      setMessageModalAttributes(
        "Your bid is too low",
        "Please go back and place a bid higher than the top bid",
        "Ok!"
      );
      return;
    }

    await approveContractForGeos({ tokenId, bid });
  };

  const getBodyContent = () => (
    <Container className="place-bid-modal">
      <Row className="mb-3">
        <Col xs={12}>
          <div className="text-center message-input-bid">
            Your bid must be at least a 5% greater than the previous one
          </div>
          <div className="text-center message-input-bid mb-3">
            MIN BID VALUE: {MIN_BID_VALUE} GEO$
          </div>
          <div className="background-input-bid">
            <input
              type="number"
              min={0}
              placeholder="00.00"
              className="w-100"
              onKeyPress={onKeyPressBid}
              onChange={onChangeBid}
            />
          </div>
          <div className="text-center message-input-bid mt-3">
            There are 2 transactions that require your approval when you are
            making a bid, you need to approve GEO$ and then approve the bid on
            your metamask. Please allow 1 minute while your bid is being
            processed.
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <button
            className="bid-confirm-button w-100"
            onClick={() => onClickConfirm()}
            disabled={hasBidError}
          >
            Confirm
          </button>
        </Col>
      </Row>
    </Container>
  );

  return (
    <>
      <ErrorModal
        message={errorMessage}
        displayModal={displayErrorModal}
        setDisplayModal={setDisplayErrorModal}
      />
      <MessageModal
        displayModal={displayMessageModal}
        setDisplayModal={setDisplayMessageModal}
        title={messageModalTitle}
        message={messageModalBody}
        buttonText={messageModalButtonText}
        buttonHandler={
          handlerQuickswapFunction
            ? redirectToQuickSwap
            : handlerBidSuccessFunction
            ? redirectToHome
            : false
        }
      />
      <Modal
        size="md"
        title="Place Your Bid"
        bodyContent={getBodyContent()}
        showFooter={false}
        displayModal={displayModal}
        setDisplayModal={setDisplayModal}
      />
    </>
  );
};

export default PlaceBidModal;
